import { useState } from "react";
import Button, {
  ButtonColorTypes,
  ButtonTypes,
} from "../shared-components/Button";
import InputField from "../shared-components/InputField";
import { classNames } from "../utils";
import { useCreateProfile, useValidateProfile } from "../api/ProfilesApi";
import { getProfileUrlValidation } from "../utils/personFieldLimits";
import { ReportSummaryItemApi } from "../api/Reports";
import { HARDCODED_ACTOR_UUID } from "../api/helpers";
import { Modal } from "../shared-components/Modal";
import { PlusIcon } from "@heroicons/react/24/solid";
import { useApplicationToast } from "./ApplicationToastProvider";
import AddItemIcon from "../shared-components/icons/AddItem";
import { ShieldExclamationIcon } from "@heroicons/react/24/outline";

type Props = {
  report: ReportSummaryItemApi;
};

const ButtonWebContentAddProfile = ({ report }: Props) => {
  const { mutate: createProfile, isLoading: isLoadingCreateProfile } =
    useCreateProfile();
  const { mutateAsync: validateProfileAsync, isLoading: isValidatingProfile } =
    useValidateProfile();
  const { showLargeToast, showPushToast } = useApplicationToast();

  const [inputUrl, setInputUrl] = useState("");
  const [invalidUrl, setInvalidUrl] = useState(false);
  const [urlError, setUrlError] = useState("Please enter a valid URL");
  const [showModal, setShowModal] = useState(false);

  const handleManualAdd = (inputUrl: string) => {
    if (!report.report_uuid || !inputUrl) {
      return;
    }
    createProfile({
      profile_url: inputUrl,
      report_uuid: report.report_uuid,
      actor_uuid: HARDCODED_ACTOR_UUID,
    });

    setShowModal(false);
    showLargeToast?.({
      title: "Added!",
      message: "The Profile has been successfully added to the report. ",
      icon: (
        <div className="bg-green-100 text-green-600 rounded-full p-2">
          <AddItemIcon className="w-6" />
        </div>
      ),
    });
  };

  const handleSubmit = async () => {
    const local = getProfileUrlValidation(inputUrl);
    if (!local.ok) {
      setUrlError(local.message);
      setInvalidUrl(true);
      return;
    }
    let toAdd = local.normalized;
    try {
      const response = await validateProfileAsync(toAdd);
      if (response.clean_url) {
        toAdd = response.clean_url;
        setInputUrl(toAdd);
      }
      if (!response.valid) {
        setUrlError(response.error || "Validation failed");
        setInvalidUrl(true);
        return;
      }
    } catch {
      showPushToast?.({
        message:
          "Could not preview the profile source. Adding the cleaned URL.",
        type: "warning",
        autoDismiss: true,
      });
    }
    handleManualAdd(toAdd);
    setInputUrl("");
    setInvalidUrl(false);
  };

  const close = () => {
    setInputUrl("");
    setInvalidUrl(false);
    setShowModal(false);
  };

  return (
    <>
      <Button
        text={
          <div className="flex gap-3 items-center py-0.5 text-gray-500">
            <PlusIcon className="w-5" />
            <span className="text-gray-700">Add Profile</span>
          </div>
        }
        colorType={ButtonColorTypes.cancel}
        onClick={() => {
          setShowModal(true);
        }}
      />
      <Modal open={showModal} onClose={close} width={"w-full max-w-lg p-3"}>
        <div className="flex gap-2">
          <div
            className={
              "bg-green-100 flex-0 rounded-full w-10 h-10 text-center flex align-middle justify-center"
            }
          >
            <PlusIcon className="w-5  text-green-600 inline" />
          </div>
          <div className="flex flex-1 flex-col gap-2 text-sm ">
            <div className="text-2xl gap-3">Add Profile</div>
            <div className="text-gray-500 flex flex-col gap-3">
              Are you sure you want to add this profile? By proceeding, this
              profile will be:
              <ul className="list-disc ml-6">
                <li>Added to your report</li>
                <li className="list-item">
                  Will not be analyzed for Flags or Keywords
                </li>
              </ul>
              <InputField
                handleChange={(e) => {
                  const value = e.target.value;
                  setInputUrl(value);
                  const local = getProfileUrlValidation(value, {
                    allowEmpty: true,
                  });
                  if (local.ok) {
                    setInvalidUrl(false);
                  } else {
                    setUrlError(local.message);
                    setInvalidUrl(true);
                  }
                }}
                name={"addedProfile"}
                label="URL"
                placeholder="Type URL here.."
                classes="w-full"
                className={classNames(
                  "mb-0  w-full",
                  invalidUrl ? "border-red-500" : ""
                )}
                value={inputUrl}
              />
              {invalidUrl && (
                <div className="flex flex-row gap-3 items-center p-1 text-sm content-center text-red-700 bg-red-100 rounded-md">
                  <ShieldExclamationIcon className="w-10"></ShieldExclamationIcon>
                  {urlError}
                </div>
              )}
              <div className="flex gap-5 justify-end">
                <Button
                  text="Cancel"
                  onClick={close}
                  colorType={ButtonColorTypes.cancel}
                  type={ButtonTypes.default}
                />
                <Button
                  text={isLoadingCreateProfile ? "Saving" : "Ok"}
                  onClick={handleSubmit}
                  className="w-20"
                  disabled={isLoadingCreateProfile || isValidatingProfile}
                  colorType={ButtonColorTypes.success}
                  type={
                    isLoadingCreateProfile
                      ? ButtonTypes.saving
                      : ButtonTypes.default
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ButtonWebContentAddProfile;
